import React from 'react';
import { Link } from 'react-router-dom';
import { Check as CheckIcon } from '@mui/icons-material';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import ProgressBar from 'components/ProgressBar';
import { hexToRgba } from 'components/cefrMeta';

interface ISubCategoryRowProps {
  index: number;
  title: string;
  totalWords: number;
  addedWords: number;
  learnedWords: number;
  color: string;
  link: string;
}

const SubCategoryRow: React.FC<ISubCategoryRowProps> = ({
  index,
  title,
  totalWords,
  addedWords,
  learnedWords,
  color,
  link,
}) => {
  const done = totalWords > 0 && learnedWords >= totalWords;

  return (
    <Link style={{ display: 'block', width: '100%' }} to={link}>
      <UIBox
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          display: 'flex',
          gap: { xs: 1.5, md: 2.25 },
          p: { xs: '10px 12px', md: '12px 18px' },
          transition: 'border-color .16s ease, background .16s ease',
          '&:hover': {
            bgcolor: hexToRgba(color, 0.04),
            borderColor: color,
          },
          '&:hover .sc-arrow': {
            transform: 'translateX(4px)',
          },
        }}
      >
        {/* Numbered badge / done checkmark */}
        <UIBox
          sx={{
            alignItems: 'center',
            bgcolor: done ? 'success.main' : hexToRgba(color, 0.12),
            border: done ? 'none' : '1px solid',
            borderColor: done ? 'transparent' : hexToRgba(color, 0.25),
            borderRadius: '50%',
            color: done ? '#fff' : color,
            display: 'inline-flex',
            flex: 'none',
            fontSize: 15,
            fontWeight: 800,
            height: 38,
            justifyContent: 'center',
            width: 38,
          }}
        >
          {done ? <CheckIcon sx={{ fontSize: 19 }} /> : index + 1}
        </UIBox>

        {/* Title + word count */}
        <UIBox sx={{ flex: 'none', minWidth: 0, width: { xs: 'auto', md: 220 } }}>
          <UIText
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </UIText>
          <UIText
            color={'primary.subtitle'}
            sx={{ fontSize: 13, mt: 0.25 }}
          >
            {`${totalWords} слів`}
          </UIText>
        </UIBox>

        {/* Inline progress */}
        <UIBox sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minWidth: 0 }}>
          <ProgressBar
            addedValue={addedWords}
            learnedValue={learnedWords}
            totalValue={totalWords}
            minWidth={'100%'}
            maxWidth={'100%'}
            width={'100%'}
          />
        </UIBox>

        <UIBox
          className={'sc-arrow'}
          component={'span'}
          sx={{
            color,
            display: 'inline-block',
            flex: 'none',
            fontSize: 20,
            fontWeight: 700,
            transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {'→'}
        </UIBox>
      </UIBox>
    </Link>
  );
};

export default SubCategoryRow;
